import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen, ChevronDown, ChevronUp, Edit3, GraduationCap, Plus, Trash2, X,
  ClipboardList, Award, Save, AlertCircle, Loader2,
  FileBadge, Upload, ExternalLink,
} from 'lucide-react';
import { adminApi } from '../services/api';
import EtatVide from '../components/EtatVide';
import {
  annoncerErreur, annoncerSucces, ChargeurLocal, confirmer, EtatErreur, Squelette, SqueletteCarte,
  SqueletteListe, SqueletteTableau, SqueletteTexte, validerFichier,
} from '../etats';

const TYPE_ICONS = { video: '🎬', fiche: '📄', quiz: '❓', exercice: '✏️', lien: '🔗' };

// ── Onglet Modules ─────────────────────────────────────────────────────────────

function ModuleForm({ module, prerempli, onSave, onClose }) {
  const [form, setForm] = useState({
    titre: module?.titre || prerempli?.titre || '',
    domaine: module?.domaine || '',
    domaine_nom: module?.domaine_nom || '',
    description: module?.description || prerempli?.resume || '',
    ordre: module?.ordre ?? 0,
    ref_externe: module?.ref_externe || prerempli?.nom_court || '',
    lien_externe: module?.lien_externe || prerempli?.url || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [domaines, setDomaines] = useState([]);

  useEffect(() => {
    let vivant = true;
    adminApi.getDomaines()
      .then((d) => { if (vivant) setDomaines(Array.isArray(d) ? d : []); })
      .catch(() => { if (vivant) setDomaines([]); });
    return () => { vivant = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await onSave({ ...form, id: module?.id });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {/* Trois parties, et ce n'est pas un raffinement : le formulaire fait
          un millier de pixels de haut. Sans hauteur plafonnée ni zone
          défilante, `items-center` sortait le titre PAR LE HAUT de l'écran
          et les boutons par le bas — sur un portable, on ne pouvait ni lire
          l'intitulé ni atteindre « Enregistrer ». Même découpe que la
          modale « Nouvelle question » plus bas. */}
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-lg shadow-carte
                   max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]
                        shrink-0 flex-wrap gap-3">
          <h3 className="text-xl font-semibold text-[var(--text-bold)]">
            {module ? 'Modifier le module' : 'Nouveau module'}
          </h3>
          <button onClick={onClose} className="act act-icone act-tertiaire">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {error && (
            <div className="encart encart-alerte">{error}</div>
          )}
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-1.5">
              Titre <span className="text-[var(--alerte)]">*</span>
            </label>
            <input
              required
              value={form.titre}
              onChange={e => setForm(p => ({ ...p, titre: e.target.value }))}
              className="champ"
            />
          </div>
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-1.5">
              Domaine <span className="text-[var(--alerte)]">*</span>
            </label>
            <select
              required
              value={form.domaine || ''}
              onChange={e => setForm(p => ({ ...p, domaine: e.target.value }))}
              className="champ"
            >
              <option value="">— Choisir un domaine —</option>
              {domaines.map(d => (
                <option key={d.id} value={d.id}>{d.nom}</option>
              ))}
            </select>
            <p className="text-[11.5px] text-[var(--text-muted)] mt-1.5">
              Le domaine se choisit dans le référentiel, il ne se saisit pas :
              c’est lui qui décide des modules reçus par un bénéficiaire ayant
              pris un seul domaine. Deux libellés voisins feraient deux
              domaines différents.
            </p>
            {!form.domaine && form.domaine_nom && (
              <p className="text-[11.5px] text-[var(--attention)] mt-1.5">
                Ce module porte le libellé « {form.domaine_nom} » sans être
                rattaché. Choisissez le domaine correspondant.
              </p>
            )}
          </div>
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={3}
              className="champ resize-none"
            />
          </div>
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-1.5">Ordre d'affichage</label>
            <input
              type="number"
              min="0"
              value={form.ordre}
              onChange={e => setForm(p => ({ ...p, ordre: parseInt(e.target.value) || 0 }))}
              className="champ w-32"
            />
          </div>

          {/* La liaison avec l'Academy. Le contenu du module vit là-bas ;
              ici on ne garde que de quoi le retrouver et l'ouvrir. */}
          <div className="encart encart-info">
            Le contenu du module vit sur l’Academy. Ces deux champs suffisent
            à le retrouver et à l’ouvrir. Le plus simple est de partir du
            bouton <strong>Catalogue Academy</strong> : il les remplit pour vous.
          </div>
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-1.5">
              Réf. externe (nom court du cours sur l’Academy)
            </label>
            <input
              value={form.ref_externe}
              onChange={e => setForm(p => ({ ...p, ref_externe: e.target.value }))}
              placeholder="ex : ASSO-M3"
              className="champ"
            />
            <p className="text-[11.5px] text-[var(--text-muted)] mt-1.5">
              C’est par elle que l’avancement remonte. Sans elle, le module
              restera à « À faire » quoi que la personne fasse sur l’Academy.
            </p>
          </div>
          <div>
            <label className="block surtitre text-[var(--text-muted)] mb-1.5">
              Lien externe
            </label>
            <input
              type="url"
              value={form.lien_externe}
              onChange={e => setForm(p => ({ ...p, lien_externe: e.target.value }))}
              placeholder="https://academy.impactlab-cilis.org/course/view.php?id=12"
              className="champ"
            />
            <p className="text-[11.5px] text-[var(--text-muted)] mt-1.5">
              Facultatif : à défaut, l’adresse est déduite de la référence.
              Sans l’un ni l’autre, le bouton affiche « Bientôt disponible ».
            </p>
          </div>
          </div>

          {/* Hors de la zone défilante : « Enregistrer » reste sous la main
              quelle que soit la longueur du formulaire. */}
          <div className="flex gap-3 p-6 border-t border-[var(--border)] shrink-0">
            <button type="button" onClick={onClose}
              className="act act-contour flex-1">
              Annuler
            </button>
            <button type="submit" disabled={loading} aria-busy={loading || undefined}
              className="act flex-1">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function ModuleRow({ module, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-[var(--bg-card)] rounded-pastille shadow-carte overflow-hidden">
      <div className="flex items-center justify-between p-5 flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-pastille bg-[var(--accent)]/15 flex items-center justify-center shrink-0">
            <span className="text-lg">{module.ordre + 1}</span>
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-[var(--text-bold)] truncate">{module.titre}</p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {/* Rattaché : pastille d'accent. Non rattaché : le dire, car
                  le module n'atteindra personne ayant choisi un domaine
                  unique — et rien d'autre ne le signale à l'écran. */}
              {module.domaine ? (
                <span className="text-[12px] font-semibold px-2 py-0.5 rounded-controle bg-[var(--accent)]/10 text-[var(--accent-texte)]">
                  {module.domaine_nom}
                </span>
              ) : (
                <span
                  title="Ce module n'est rattaché à aucun domaine du référentiel : il ne sera pas proposé à un bénéficiaire ayant choisi un domaine unique."
                  className="text-[12px] font-semibold px-2 py-0.5 rounded-controle bg-[var(--attention-fond)] text-[var(--attention)]">
                  Domaine à rattacher
                </span>
              )}
              {!module.ref_externe && (
                <span
                  title="Sans référence Academy, l'avancement ne peut pas remonter : le module restera à « À faire »."
                  className="text-[12px] font-semibold px-2 py-0.5 rounded-controle bg-[var(--attention-fond)] text-[var(--attention)]">
                  Réf. Academy manquante
                </span>
              )}
              <span className="text-xs text-[var(--text-muted)]">{module.nb_ressources} ressource{module.nb_ressources > 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onEdit(module)} className="act act-icone act-tertiaire">
            <Edit3 className="w-4 h-4" />
          </button>
          <button onClick={() => onDelete(module)} className="act act-icone act-tertiaire">
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="act act-icone act-tertiaire">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-[var(--border)]">
          {module.description && (
            <p className="text-sm text-[var(--text-muted)] mt-4 mb-4">{module.description}</p>
          )}
          {module.ressources?.length > 0 ? (
            <div className="space-y-2">
              <p className="surtitre text-[var(--text-muted)] mb-2">Ressources</p>
              {module.ressources.map(r => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-3 rounded-pastille bg-[var(--bg-main)] border border-[var(--border)]">
                  <span className="text-base">{TYPE_ICONS[r.type_ressource] || '📎'}</span>
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-bold)]">{r.titre}</p>
                    <p className="text-xs text-[var(--text-muted)] capitalize">{r.type_ressource}{r.duree_minutes > 0 ? ` · ${r.duree_minutes} min` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--text-muted)] mt-4">Aucune ressource encore associée à ce module.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Le catalogue vivant de l'Academy ─────────────────────────────────
   Ajouter un module en retapant son titre et sa référence, c'est se
   tromper un jour d'un caractère sur la clé par laquelle l'avancement
   remonte — et chercher longtemps pourquoi un apprenant reste à « À
   faire ». On part donc du cours tel qu'il existe là-bas.

   L'Academy injoignable ne casse rien : le panneau le dit et l'on continue
   à saisir les fiches à la main. */
function CatalogueAcademy({ onImporter, onClose }) {
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    let vivant = true;
    adminApi.getCatalogueAcademy()
      .then((d) => { if (vivant) setEtat(d || { cours: [] }); })
      .catch((e) => { if (vivant) setErreur(e); });
    return () => { vivant = false; };
  }, []);

  const cours = etat?.cours || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-2xl p-8 shadow-carte
                   max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <h3 className="text-xl font-semibold text-[var(--text-bold)]">Catalogue Academy</h3>
          <button onClick={onClose} className="act act-icone act-tertiaire" aria-label="Fermer">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>
        <p className="text-[13px] text-[var(--text-muted)] mb-5">
          Les cours tels qu’ils existent sur l’Academy. « Importer » remplit
          la fiche&nbsp;: titre, référence et lien.
        </p>

        {erreur && <EtatErreur erreur={erreur} compact />}
        {etat && etat.configuree === false && (
          <div className="encart encart-info">
            L’Academy n’est pas configurée sur ce serveur. Les fiches se
            saisissent à la main en attendant.
          </div>
        )}
        {etat?.erreur && (
          <div className="encart encart-alerte mb-4">
            L’Academy n’a pas répondu&nbsp;: {etat.erreur}
          </div>
        )}

        {!etat && !erreur && <ChargeurLocal texte="Lecture du catalogue…" className="py-10" />}

        {etat?.configuree && cours.length === 0 && !etat.erreur && (
          <div className="encart encart-info">
            Aucun cours dans la catégorie ASSO-PRO de l’Academy.
          </div>
        )}

        <div className="overflow-y-auto -mx-2 px-2">
          {cours.map((c) => (
            <div key={c.id}
                 className="flex items-center gap-3 py-3 border-b border-[var(--border)] last:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-medium text-[var(--text-bold)] truncate">
                  {c.titre}
                </p>
                <p className="text-[11.5px] text-[var(--text-muted)]">{c.nom_court}</p>
              </div>
              {c.importe ? (
                <span className="text-[11.5px] text-[var(--text-muted)] shrink-0">Déjà importé</span>
              ) : (
                <button type="button" className="act act-contour shrink-0"
                        onClick={() => onImporter(c)}>
                  Importer
                </button>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function TabModules() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const dejaCharge = useRef(false);
  const [error, setError] = useState(null);
  const [editModule, setEditModule] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [catalogue, setCatalogue] = useState(false);
  // Le cours choisi dans le catalogue, qui préremplit la fiche.
  const [prerempli, setPrerempli] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getModules();
      setModules(Array.isArray(data) ? data : []);
      setError(null);
      dejaCharge.current = true;
      setCharge(true);
    } catch (e) {
      // Une actualisation qui échoue ne retire pas la liste affichée.
      if (dejaCharge.current) annoncerErreur(e.message || 'Les modules n’ont pas pu être actualisés.');
      else setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // L'échec d'un enregistrement s'affiche dans le formulaire, qui reste ouvert.
  const handleSave = async (form) => {
    if (form.id) await adminApi.updateModule(form.id, form);
    else await adminApi.createModule(form);
    annoncerSucces(form.id ? 'Module enregistré.' : `Module « ${form.titre} » créé.`);
    load();
  };

  const handleDelete = async (module) => {
    if (!await confirmer(`Supprimer le module "${module.titre}" ?`)) return;
    try {
      await adminApi.deleteModule(module.id);
      annoncerSucces(`Module « ${module.titre} » supprimé.`);
      load();
    } catch (e) {
      annoncerErreur(e.message || 'Le module n’a pas pu être supprimé.');
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        {loading && !charge
          ? <Squelette className="h-3 w-20" />
          : <p className="text-sm text-[var(--text-muted)]">{modules.length} module{modules.length > 1 ? 's' : ''}</p>}
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => setCatalogue(true)} className="act act-contour">
            Catalogue Academy
          </button>
          <button
            onClick={() => { setEditModule(null); setPrerempli(null); setShowForm(true); }}
            className="act"
          >
            <Plus className="w-4 h-4" />
            Nouveau module
          </button>
        </div>
      </div>

      {loading && !charge ? (
        <SqueletteListe lignes={4} />
      ) : error && !charge ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte"><EtatErreur erreur={error} onReessayer={load} /></div>
      ) : modules.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide
            icone={BookOpen}
            titre="Aucun module de formation"
            aide="Le parcours est vide : ajoutez un premier module pour qu'il puisse être suivi."
            action={(
              <button type="button" className="act"
                      onClick={() => { setEditModule(null); setShowForm(true); }}>
                Créer le premier module
              </button>
            )}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {modules.map(m => (
            <ModuleRow
              key={m.id}
              module={m}
              onEdit={mod => { setEditModule(mod); setShowForm(true); }}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {showForm && (
        <ModuleForm
          module={editModule}
          prerempli={prerempli}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditModule(null); setPrerempli(null); }}
        />
      )}

      {catalogue && (
        <CatalogueAcademy
          onClose={() => setCatalogue(false)}
          onImporter={(c) => {
            // On ne crée pas la fiche dans le dos : on ouvre le formulaire
            // prérempli, pour que le domaine et l'ordre soient renseignés
            // avant l'enregistrement.
            setCatalogue(false);
            setEditModule(null);
            setPrerempli(c);
            setShowForm(true);
          }}
        />
      )}
    </>
  );
}

// ── Onglet Examen Final ────────────────────────────────────────────────────────

function QuestionForm({ question, onSave, onClose }) {
  const [form, setForm] = useState({
    question: question?.question || '',
    choix: question?.choix?.length ? question.choix : ['', ''],
    bonne_reponse: question?.bonne_reponse || '',
    points: question?.points ?? 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const addChoix = () => setForm(p => ({ ...p, choix: [...p.choix, ''] }));
  const removeChoix = (i) => setForm(p => ({ ...p, choix: p.choix.filter((_, idx) => idx !== i), bonne_reponse: p.bonne_reponse === p.choix[i] ? '' : p.bonne_reponse }));
  const updateChoix = (i, val) => setForm(p => { const c = [...p.choix]; c[i] = val; return { ...p, choix: c }; });

  const submit = async (e) => {
    e.preventDefault();
    if (form.choix.filter(c => c.trim()).length < 2) { setError('Au moins 2 choix de réponse requis.'); return; }
    if (!form.bonne_reponse) { setError('Sélectionnez la bonne réponse.'); return; }
    setLoading(true);
    setError('');
    try {
      await onSave({ ...form, choix: form.choix.filter(c => c.trim()) });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-[var(--bg-card)] rounded-carte w-full max-w-xl shadow-carte max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)] shrink-0 flex-wrap gap-3">
          <h3 className="text-xl font-semibold text-[var(--text-bold)]">
            {question ? 'Modifier la question' : 'Nouvelle question'}
          </h3>
          <button onClick={onClose} className="act act-icone act-tertiaire">
            <X className="w-5 h-5 text-[var(--text-muted)]" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {error && <div className="encart encart-alerte mb-4">{error}</div>}

          <form id="q-form" onSubmit={submit} className="space-y-4">
            <div>
              <label className="block surtitre text-[var(--text-muted)] mb-1.5">
                Question <span className="text-[var(--alerte)]">*</span>
              </label>
              <textarea
                required
                value={form.question}
                onChange={e => setForm(p => ({ ...p, question: e.target.value }))}
                rows={3}
                className="champ resize-none"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
                <label className="sous-titre-page">
                  Choix de réponse <span className="text-[var(--alerte)]">*</span>
                </label>
                <button type="button" onClick={addChoix} className="act act-tertiaire">
                  + Ajouter
                </button>
              </div>
              <div className="space-y-2">
                {form.choix.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="bonne_reponse"
                      checked={form.bonne_reponse === c && c.trim() !== ''}
                      onChange={() => c.trim() && setForm(p => ({ ...p, bonne_reponse: c }))}
                      className="accent-[var(--accent)] w-4 h-4 shrink-0"
                      title="Sélectionner comme bonne réponse"
                    />
                    <input
                      value={c}
                      onChange={e => updateChoix(i, e.target.value)}
                      onBlur={() => {
                        if (form.bonne_reponse === form.choix[i]) setForm(p => ({ ...p, bonne_reponse: c }));
                      }}
                      placeholder={`Choix ${i + 1}`}
                      className="champ flex-1"
                    />
                    {form.choix.length > 2 && (
                      <button type="button" onClick={() => removeChoix(i)} className="act act-icone act-tertiaire">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {form.bonne_reponse && (
                <p className="mt-2 text-[10px] font-semibold text-[var(--accent-texte)]">
                  ✓ Bonne réponse : {form.bonne_reponse}
                </p>
              )}
            </div>

            <div>
              <label className="block surtitre text-[var(--text-muted)] mb-1.5">Points</label>
              <input
                type="number"
                min="1"
                value={form.points}
                onChange={e => setForm(p => ({ ...p, points: parseInt(e.target.value) || 1 }))}
                className="champ w-24"
              />
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-[var(--border)] flex gap-3 shrink-0">
          <button type="button" onClick={onClose}
            className="act act-contour flex-1">
            Annuler
          </button>
          <button form="q-form" type="submit" disabled={loading} aria-busy={loading || undefined}
            className="act flex-1">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function TabExamen() {
  const [typeProjet, setTypeProjet] = useState('formation');
  const [examen, setExamen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const dejaCharge = useRef(false);
  const [erreurChargement, setErreurChargement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showQForm, setShowQForm] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);

  // Config form
  const [cfg, setCfg] = useState({ titre: '', description: '', duree_minutes: 60, nb_tentatives_max: 3, actif: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getExamenConfig(typeProjet);
      if (data.exists) {
        setExamen(data);
        setCfg({
          titre: data.titre,
          description: data.description || '',
          duree_minutes: data.duree_minutes,
          nb_tentatives_max: data.nb_tentatives_max,
          actif: data.actif,
        });
      } else {
        setExamen(null);
        setCfg({ titre: `Examen final — ${typeProjet}`, description: '', duree_minutes: 60, nb_tentatives_max: 3, actif: true });
      }
      setErreurChargement(null);
      dejaCharge.current = true;
      setCharge(true);
    } catch (e) {
      if (dejaCharge.current) annoncerErreur(e.message || 'L’examen n’a pas pu être chargé.');
      else setErreurChargement(e);
    } finally {
      setLoading(false);
    }
  }, [typeProjet]);

  useEffect(() => { load(); }, [load]);

  const handleSaveCfg = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await adminApi.saveExamenConfig(typeProjet, cfg);
      annoncerSucces('Configuration de l’examen enregistrée.');
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveQuestion = async (form) => {
    if (editQuestion) {
      await adminApi.updateQuestionExamen(editQuestion.id, form);
    } else {
      await adminApi.ajouterQuestionExamen(typeProjet, form);
    }
    annoncerSucces(editQuestion ? 'Question enregistrée.' : 'Question ajoutée.');
    load();
  };

  const handleDeleteQuestion = async (q) => {
    if (!await confirmer(`Supprimer la question "${q.question.slice(0, 60)}..." ?`)) return;
    try {
      await adminApi.deleteQuestionExamen(q.id);
      annoncerSucces('Question supprimée.');
      load();
    } catch (e) {
      annoncerErreur(e.message || 'La question n’a pas pu être supprimée.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Type selector */}
      <div className="flex gap-2">
        {['formation', 'certification'].map(t => (
          <button
            key={t}
            onClick={() => setTypeProjet(t)}
            className={`px-5 py-2.5 rounded-pastille text-[13px] font-semibold transition-all border ${
              typeProjet === t
                ? 'bg-[var(--accent)] text-[#0A1128] border-transparent'
                : 'border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-main)]'
            }`}
          >
            {t === 'formation' ? 'Formation' : 'Certification'}
          </button>
        ))}
      </div>

      {loading && !charge ? (
        <>
          <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6">
            <Squelette className="h-4 w-56 mb-5" />
            <div className="grid sm:grid-cols-2 gap-4">
              {[0, 1, 2, 3].map(i => <Squelette key={i} className="h-10 w-full" />)}
            </div>
          </div>
          <SqueletteListe lignes={3} />
        </>
      ) : erreurChargement && !charge ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte"><EtatErreur erreur={erreurChargement} onReessayer={load} /></div>
      ) : (
        <>
          {/* Config card */}
          <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6">
            <h3 className="titre-carte mb-5">
              Configuration de l'examen
            </h3>
            <form onSubmit={handleSaveCfg} className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block surtitre text-[var(--text-muted)] mb-1.5">Titre</label>
                <input
                  value={cfg.titre}
                  onChange={e => setCfg(p => ({ ...p, titre: e.target.value }))}
                  className="champ"
                />
              </div>
              <div>
                <label className="block surtitre text-[var(--text-muted)] mb-1.5">Durée (min)</label>
                <input
                  type="number" min="5"
                  value={cfg.duree_minutes}
                  onChange={e => setCfg(p => ({ ...p, duree_minutes: parseInt(e.target.value) || 60 }))}
                  className="champ"
                />
              </div>
              <div>
                <label className="block surtitre text-[var(--text-muted)] mb-1.5">Tentatives max</label>
                <input
                  type="number" min="1"
                  value={cfg.nb_tentatives_max}
                  onChange={e => setCfg(p => ({ ...p, nb_tentatives_max: parseInt(e.target.value) || 3 }))}
                  className="champ"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block surtitre text-[var(--text-muted)] mb-1.5">Description</label>
                <textarea
                  value={cfg.description}
                  onChange={e => setCfg(p => ({ ...p, description: e.target.value }))}
                  rows={2}
                  className="champ resize-none"
                />
              </div>
              <div className="sm:col-span-2 flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cfg.actif}
                    onChange={e => setCfg(p => ({ ...p, actif: e.target.checked }))}
                    className="w-4 h-4 accent-[var(--accent)]"
                  />
                  <span className="text-sm font-semibold text-[var(--text-main)]">Examen actif</span>
                </label>
                {/* Pendant qu'une autre nature se charge, la fiche affichée
                    n'est pas encore la sienne : on ne l'enregistre pas. */}
                <button type="submit" disabled={saving || loading} aria-busy={saving || undefined}
                  className="act ml-auto">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </button>
              </div>
            </form>
            {error && <p className="mt-3 text-sm text-[var(--alerte)] font-semibold flex items-center gap-1.5"><AlertCircle className="w-4 h-4" />{error}</p>}
          </div>

          {/* Questions */}
          {examen && (
            <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <h3 className="titre-carte">
                  Questions ({examen.nb_questions})
                </h3>
                <button
                  onClick={() => { setEditQuestion(null); setShowQForm(true); }}
                  className="act"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Ajouter
                </button>
              </div>

              {examen.questions?.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] text-center py-8">Aucune question encore ajoutée.</p>
              ) : (
                <div className="space-y-3">
                  {examen.questions?.map((q, i) => (
                    <div key={q.id} className="flex items-start gap-4 p-4 rounded-pastille bg-[var(--bg-main)] border border-[var(--border)]">
                      <span className="w-6 h-6 rounded-controle bg-[var(--accent)]/20 text-[var(--accent-texte)] text-[11px] font-semibold flex items-center justify-center shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[var(--text-bold)] mb-2">{q.question}</p>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {q.choix?.map((c, ci) => (
                            <span key={ci}
                              className={`px-2.5 py-1 rounded-controle text-[10px] font-semibold ${
                                c === q.bonne_reponse
                                  ? 'bg-[var(--vert-pale)] text-[var(--vert-fonce)]'
                                  : 'bg-[var(--bg-card)] text-[var(--text-muted)]'
                              }`}>
                              {c === q.bonne_reponse && '✓ '}{c}
                            </span>
                          ))}
                        </div>
                        <p className="text-[10px] text-[var(--text-muted)]">{q.points} point{q.points > 1 ? 's' : ''}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => { setEditQuestion(q); setShowQForm(true); }}
                          className="act act-icone act-tertiaire">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDeleteQuestion(q)}
                          className="act act-icone act-tertiaire">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showQForm && (
        <QuestionForm
          question={editQuestion}
          onSave={handleSaveQuestion}
          onClose={() => { setShowQForm(false); setEditQuestion(null); }}
        />
      )}
    </div>
  );
}

// ── Onglet Conditions ──────────────────────────────────────────────────────────

function TabConditions() {
  const [typeProjet, setTypeProjet] = useState('formation');
  const [form, setForm] = useState({ source_epreuve: 'questionnaire', score_min_quiz: 60,
                                     score_min_examen: 60, taux_completion_min: 100, description: '' });
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [charge, setCharge] = useState(false);       // au moins une réponse reçue
  const dejaCharge = useRef(false);
  const [erreurChargement, setErreurChargement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.getConditionsCertif(typeProjet);
      setSources(data.sources_epreuve || []);
      setForm({
        source_epreuve: data.source_epreuve || 'questionnaire',
        score_min_quiz: data.score_min_quiz ?? 60,
        score_min_examen: data.score_min_examen ?? 60,
        taux_completion_min: data.taux_completion_min ?? 100,
        description: data.description || '',
      });
      setErreurChargement(null);
      dejaCharge.current = true;
      setCharge(true);
    } catch (e) {
      if (dejaCharge.current) annoncerErreur(e.message || 'Les conditions n’ont pas pu être chargées.');
      else setErreurChargement(e);
    } finally {
      setLoading(false);
    }
  }, [typeProjet]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await adminApi.updateConditionsCertif(typeProjet, form);
      annoncerSucces('Conditions d’obtention enregistrées.');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Type selector */}
      <div className="flex gap-2">
        {['formation', 'certification'].map(t => (
          <button
            key={t}
            onClick={() => setTypeProjet(t)}
            className={`px-5 py-2.5 rounded-pastille text-[13px] font-semibold transition-all border ${
              typeProjet === t
                ? 'bg-[var(--accent)] text-[#0A1128] border-transparent'
                : 'border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-main)]'
            }`}
          >
            {t === 'formation' ? 'Formation' : 'Certification'}
          </button>
        ))}
      </div>

      {loading && !charge ? (
        <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6">
          <Squelette className="h-4 w-72 mb-6" />
          <SqueletteTexte lignes={2} className="mb-5" />
          <div className="grid sm:grid-cols-3 gap-4">
            {[0, 1, 2].map(i => <Squelette key={i} className="h-10 w-full" />)}
          </div>
        </div>
      ) : erreurChargement && !charge ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte"><EtatErreur erreur={erreurChargement} onReessayer={load} /></div>
      ) : (
        <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6">
          <h3 className="titre-carte mb-6">
            Conditions d'obtention du certificat — {typeProjet}
          </h3>

          <form onSubmit={handleSave} className="space-y-5">
            {/* ── Ce qui s'applique, et seulement cela ───────────────────────
                Les trois critères étaient proposés à la saisie pour les deux
                parcours, alors que le serveur n'en applique qu'un en
                certification : ce parcours ne comporte pas de modules, donc
                ni quiz ni taux de complétion (voir `examen_disponible` dans
                `accompagnement/views.py`). L'examen y est ouvert d'emblée et
                seul son score décide.

                On réglait donc deux valeurs sans effet, et le résumé plus bas
                — qui, lui, disait vrai — donnait l'impression d'un affichage
                fautif. Un champ modifiable qui ne change rien est pire qu'un
                champ absent : il fait croire à un réglage. */}
            {/* ── L'épreuve retenue ─────────────────────────────────────
                Deux façons légitimes de décider d'un certificat, et elles
                ne demandent pas le même travail : le DIAGNOSTIC lui-même —
                rien à construire, le référentiel existe — ou un
                QUESTIONNAIRE bâti dans l'onglet « Examen final ».

                Le choix se fait ici, avec les seuils, parce que c'est la
                même décision : comment on accorde le certificat. */}
            {typeProjet === 'certification' && sources.length > 0 && (
              <div>
                <span className="block surtitre text-[var(--text-muted)] mb-1.5">
                  Sur quoi le certificat se décide
                </span>
                <div className="space-y-2">
                  {sources.map(([cle, libelle]) => (
                    <label key={cle} className="flex items-start gap-2 text-[13.5px]">
                      <input type="radio" name="source_epreuve" value={cle}
                             checked={form.source_epreuve === cle}
                             onChange={() => setForm(p => ({ ...p, source_epreuve: cle }))}
                             className="mt-0.5 shrink-0" />
                      <span>
                        {libelle}
                        <span className="block text-[11.5px] text-[var(--text-muted)]">
                          {cle === 'diagnostic'
                            ? "Le score du diagnostic décide. Rien à construire, et l’organisation ne passe qu’une épreuve."
                            : "Les questions de l’onglet « Examen final ». À construire avant d’ouvrir la certification."}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className={`grid gap-4 ${typeProjet === 'certification' ? 'sm:grid-cols-1' : 'sm:grid-cols-3'}`}>
              {typeProjet !== 'certification' && (
                <div>
                  <label className="block surtitre text-[var(--text-muted)] mb-1.5">
                    Score quiz min (%)
                  </label>
                  <input
                    type="number" min="0" max="100"
                    value={form.score_min_quiz}
                    onChange={e => setForm(p => ({ ...p, score_min_quiz: parseFloat(e.target.value) || 0 }))}
                    className="champ"
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">Score moyen requis sur les quiz de modules</p>
                </div>
              )}
              <div>
                <label className="block surtitre text-[var(--text-muted)] mb-1.5">
                  Score examen min (%)
                </label>
                <input
                  type="number" min="0" max="100"
                  value={form.score_min_examen}
                  onChange={e => setForm(p => ({ ...p, score_min_examen: parseFloat(e.target.value) || 0 }))}
                  className="champ"
                />
                <p className="text-[10px] text-[var(--text-muted)] mt-1">Score requis à l'examen final</p>
              </div>
              {typeProjet !== 'certification' && (
                <div>
                  <label className="block surtitre text-[var(--text-muted)] mb-1.5">
                    Taux complétion min (%)
                  </label>
                  <input
                    type="number" min="0" max="100"
                    value={form.taux_completion_min}
                    onChange={e => setForm(p => ({ ...p, taux_completion_min: parseFloat(e.target.value) || 0 }))}
                    className="champ"
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">% de modules complétés requis</p>
                </div>
              )}
            </div>

            {typeProjet === 'certification' && (
              <p className="text-[12.5px] text-[var(--text-muted)] leading-relaxed">
                Le parcours de certification ne comporte pas de modules :
                l'examen est accessible dès le départ et son score est le seul
                critère. Le score aux quiz et le taux de complétion ne
                s'appliquent qu'à la formation.
              </p>
            )}

            <div>
              <label className="block surtitre text-[var(--text-muted)] mb-1.5">
                Description (affichée au bénéficiaire)
              </label>
              <textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={3}
                placeholder="Ex : Complétez tous vos modules et réussissez l'examen pour obtenir votre certificat ASSO PRO."
                className="champ resize-none"
              />
            </div>

            {/* Résumé visuel */}
            <div className="flex flex-wrap gap-3 p-4 bg-[var(--accent)]/5 rounded-pastille border border-[var(--accent)]/20">
              {[
                { label: 'Quiz', value: `≥ ${form.score_min_quiz}%`, skip: typeProjet === 'certification' },
                { label: 'Examen', value: `≥ ${form.score_min_examen}%` },
                { label: 'Complétion modules', value: `≥ ${form.taux_completion_min}%`, skip: typeProjet === 'certification' },
              ].filter(item => !item.skip).map(item => (
                <div key={item.label} className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-card)] rounded-carte shadow-carte">
                  <Award className="w-3.5 h-3.5 text-[var(--accent-texte)]" />
                  <span className="sous-titre-page">{item.label}</span>
                  <span className="text-sm font-semibold text-[var(--text-bold)]">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3">
              {error && <p className="text-sm text-[var(--alerte)] font-semibold flex items-center gap-1.5"><AlertCircle className="w-4 h-4" />{error}</p>}
              {/* Pendant qu'une autre nature se charge, on n'enregistre pas
                  la fiche de la précédente sous son nom. */}
              <button type="submit" disabled={saving || loading} aria-busy={saving || undefined}
                className="act ml-auto">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Onglet Certificat ──────────────────────────────────────────────────────────
/* Un certificat est aujourd'hui un fichier téléversé À LA MAIN, un par
   organisation. Chaque document dépend donc de qui l'a préparé, et rien ne
   garantit que deux certificats se ressemblent.

   La direction retenue : un gabarit par nature — un fond PDF, et des zones
   où les informations du bénéficiaire viendront s'inscrire — puis un
   certificat numérique porteur d'une empreinte cryptographique.

   Ce que cet écran doit faire comprendre, et qui n'est pas intuitif :
   **déposer un nouveau fond ne remplace rien, il ouvre une version.** Un
   certificat émis avec la version 1 reste rattaché à celle-ci ; l'écraser
   ferait échouer la vérification de documents pourtant authentiques. D'où
   l'historique affiché, et la mention « figée » dès qu'une version a servi.

   La composition n'est pas écrite. La page le DIT, plutôt que de laisser
   croire qu'un certificat va sortir du gabarit déposé. */

function CarteGabarit({ nature, onDepose, peutDeposer }) {
  const actif = nature.actif;
  const [fichier, setFichier] = useState(null);
  const [nom, setNom] = useState(actif?.nom_affiche || '');
  const [notes, setNotes] = useState(actif?.notes || '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const choisir = (f) => {
    setFichier(f);
    setErreur(f ? validerFichier(f, { extensions: ['.pdf'] }) || '' : '');
  };

  const envoyer = async (e) => {
    e.preventDefault();
    const refus = fichier && validerFichier(fichier, { extensions: ['.pdf'] });
    if (refus) { setErreur(refus); return; }
    setEnvoi(true); setErreur('');
    try {
      const corps = new FormData();
      corps.append('type_projet', nature.type_projet);
      if (fichier) corps.append('fichier', fichier);
      corps.append('nom_affiche', nom);
      corps.append('notes', notes);
      const enregistre = await adminApi.deposerModeleCertificat(corps);
      setFichier(null);
      annoncerSucces(fichier
        ? `${nature.type_libelle} : version ${enregistre.version} déposée et mise en service.`
        : `${nature.type_libelle} : gabarit enregistré.`);
      onDepose();
    } catch (souci) {
      setErreur(souci.message || 'Le gabarit n’a pas pu être enregistré.');
    } finally {
      setEnvoi(false);
    }
  };

  const anciennes = (nature.versions || []).filter((v) => v.id !== actif?.id);

  return (
    <form onSubmit={envoyer} className="rounded-carte bg-[var(--bg-card)] shadow-carte p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="titre-carte">
            {nature.type_libelle}
            {actif && (
              <span className="ml-2 text-[12px] font-medium text-[var(--text-muted)]">
                version {actif.version} en service
              </span>
            )}
          </h3>
          {actif ? (
            <p className="text-[12.5px] text-[var(--text-muted)] mt-1">
              Déposée le {new Date(actif.cree_le).toLocaleDateString('fr-FR')}
              {actif.cree_par ? ` par ${actif.cree_par}` : ''}
              {actif.gele
                ? (actif.certificats_emis > 1
                  ? ` — figée : ${actif.certificats_emis} certificats en sont issus`
                  : ' — figée : un certificat en est issu')
                : ''}
            </p>
          ) : (
            <p className="text-[12.5px] text-[var(--attention)] mt-1">
              Aucun gabarit déposé pour cette nature.
            </p>
          )}
        </div>
        {actif?.url && (
          <a href={actif.url} target="_blank" rel="noopener noreferrer"
             className="act act-contour shrink-0">
            <ExternalLink className="w-4 h-4" />
            Voir le fond
          </a>
        )}
      </div>

      {erreur && <div role="alert" className="encart encart-alerte">{erreur}</div>}

      <div>
        <label className="block surtitre text-[var(--text-muted)] mb-1.5">
          Fond du certificat
        </label>
        <input
          type="file"
          accept="application/pdf,.pdf"
          disabled={!peutDeposer}
          onChange={(e) => choisir(e.target.files?.[0] || null)}
          aria-invalid={!!erreur || undefined}
          className="champ"
        />
        <p className="text-[11.5px] text-[var(--text-muted)] mt-1.5">
          Un PDF : logo, couleurs, bordures, textes fixes. Le système écrira
          par-dessus, il ne le reconstruit pas. Déposer un fond{' '}
          <strong>ouvre une nouvelle version</strong> — les certificats déjà
          émis restent rattachés à la leur. Laissez vide pour ne modifier que
          le nom ou les notes.
        </p>
      </div>

      <div>
        <label className="block surtitre text-[var(--text-muted)] mb-1.5">Nom du gabarit</label>
        <input value={nom} onChange={(e) => setNom(e.target.value)}
               disabled={!peutDeposer}
               placeholder="ex : Attestation — septembre 2026"
               className="champ" />
      </div>

      <div>
        <label className="block surtitre text-[var(--text-muted)] mb-1.5">Notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  disabled={!peutDeposer} rows={2}
                  placeholder="Ce qui a changé depuis la version précédente."
                  className="champ resize-none" />
      </div>

      {peutDeposer && (
        <button type="submit" disabled={envoi} aria-busy={envoi || undefined} className="act">
          {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {fichier ? 'Déposer une nouvelle version' : 'Enregistrer'}
        </button>
      )}

      {/* L'historique n'est pas décoratif : c'est lui qui rend visible le
          fait qu'une ancienne version reste en service pour les certificats
          qui en sont issus. */}
      {anciennes.length > 0 && (
        <div className="pt-3 border-t border-[var(--bordure)]">
          <p className="surtitre text-[var(--text-muted)] mb-2">Versions précédentes</p>
          <ul className="space-y-1.5">
            {anciennes.map((v) => (
              <li key={v.id} className="flex items-baseline justify-between gap-3 text-[12.5px]">
                <span className="text-[var(--text-bold)]">
                  Version {v.version}
                  <span className="text-[var(--text-muted)] font-normal">
                    {' '}— {v.statut_libelle.toLowerCase()},{' '}
                    {v.certificats_emis} certificat{v.certificats_emis > 1 ? 's' : ''}
                  </span>
                </span>
                {v.url && (
                  <a href={v.url} target="_blank" rel="noopener noreferrer"
                     className="text-[var(--accent)] hover:underline shrink-0">
                    Voir
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
}

function TabCertificat({ user }) {
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const dejaCharge = useRef(false);
  const peutDeposer = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role);

  const charger = useCallback(async () => {
    try {
      // `authFetch` rend ici les données parsées et lève de lui-même :
      // pas de `.ok` ni de `.json()` à attendre.
      setEtat(await adminApi.getModelesCertificat());
      setErreur(null);
      dejaCharge.current = true;
    } catch (e) {
      // Après un dépôt, un rechargement qui échoue garde les gabarits affichés.
      if (dejaCharge.current) annoncerErreur(e.message || 'Les gabarits n’ont pas pu être actualisés.');
      else setErreur(e);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const reessayer = useCallback(() => { setErreur(null); charger(); }, [charger]);

  if (erreur && !etat) {
    return <div className="rounded-carte bg-[var(--bg-card)] shadow-carte"><EtatErreur erreur={erreur} onReessayer={reessayer} /></div>;
  }
  if (!etat) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5"><SqueletteCarte /><SqueletteCarte /></div>
        <SqueletteTableau colonnes={2} lignes={4} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Dire où en est le mécanisme vaut mieux que de laisser croire qu'un
          certificat sort déjà du gabarit. */}
      {!etat.composition_active && (
        <div className="encart encart-info">
          <p>
            <strong>Le dépôt du gabarit est en place ; la composition ne l’est
            pas encore.</strong> Les informations ne s’injectent pas encore
            automatiquement, et l’empreinte cryptographique reste à définir.
            Déposer le gabarit dès maintenant permet de le préparer et de le
            versionner.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {etat.modeles.map((m) => (
          <CarteGabarit key={m.type_projet} nature={m}
                        peutDeposer={peutDeposer} onDepose={charger} />
        ))}
      </div>

      {/* Ce que le système sait fournir. Sans cette liste, on réserve dans le
          gabarit une place pour une information qui n'existe pas. */}
      <div className="carte-tableau">
        <div className="p-5 pb-0">
          <h3 className="titre-carte">Ce qui pourra être injecté</h3>
          <p className="text-[12.5px] text-[var(--text-muted)] mt-1">
            Les données que le système détient déjà. Concevez le gabarit
            là-dessus : le reste n’existe pas en base.
          </p>
        </div>
        <div className="tableau-hote">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th scope="col">Donnée</th>
                <th scope="col">Exemple</th>
              </tr>
            </thead>
            <tbody>
              {etat.donnees_disponibles.map((d) => (
                <tr key={d.cle}>
                  <th scope="row" className="font-normal">
                    <span className="text-[13.5px] font-medium text-[var(--text-bold)]">
                      {d.libelle}
                    </span>
                    <span className="block text-[11.5px] text-[var(--text-muted)]">
                      {d.cle}{d.note ? ` — ${d.note}` : ''}
                    </span>
                  </th>
                  <td className="text-[13px] text-[var(--text-muted)]">{d.exemple}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Page principale ────────────────────────────────────────────────────────────

const TABS = [
  { key: 'modules',    label: 'Modules',        Icon: BookOpen },
  { key: 'examen',     label: 'Examen final',   Icon: ClipboardList },
  { key: 'conditions', label: 'Conditions',     Icon: Award },
  { key: 'certificat', label: 'Certificat',     Icon: FileBadge },
];

export default function FormationAdmin({ user }) {
  const [activeTab, setActiveTab] = useState('modules');

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 rounded-pastille bg-[var(--accent)]/15 flex items-center justify-center">
          <GraduationCap className="w-6 h-6 text-[var(--accent-texte)]" />
        </div>
        <div>
          <h1 className="titre-page">Parcours de formation</h1>
          <p className="text-sm text-[var(--text-muted)]">Modules, examens et conditions de certification</p>
        </div>
      </div>

      {/* Tabs */}
      {/* Choix exclusif : le controle segmente du modele. Une barre
          soulignee se lisait comme une navigation vers d'autres pages. */}
      <div className="segmente max-w-full overflow-x-auto mb-6" role="group" aria-label="Sections">
        {TABS.map(({ key, label, Icon }) => (
          <button key={key} type="button" onClick={() => setActiveTab(key)}
                  aria-pressed={activeTab === key} className="whitespace-nowrap shrink-0">
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'modules' && <TabModules />}
      {activeTab === 'examen' && <TabExamen />}
      {activeTab === 'conditions' && <TabConditions />}
      {activeTab === 'certificat' && <TabCertificat user={user} />}
    </motion.div>
  );
}
